import { useState, useEffect, useCallback } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic } from './shared';
import * as api from '../lib/api';

const TIER_COLORS = { bronze: '#CD7F32', silver: '#C0C0C0', gold: GOLD, legendary: EMBER };

function HeatmapGrid({ heatmap, compact }) {
  const maxCount = Math.max(1, ...heatmap.map(m => m.count));
  return (
    <div data-testid="streak-heatmap" style={{ display: 'grid', gridTemplateColumns: `repeat(${heatmap.length}, 1fr)`, gap: compact ? 3 : 4 }}>
      {heatmap.map(m => {
        const intensity = m.count > 0 ? Math.max(0.2, m.count / maxCount) : 0;
        return (
          <div key={m.month} style={{ textAlign: 'center' }}>
            <div
              data-testid={`heatmap-cell-${m.month}`}
              title={`${m.label}: ${m.count} payments ($${m.amount.toLocaleString()})`}
              style={{
                width: '100%', aspectRatio: '1', borderRadius: 4,
                background: m.active ? `rgba(232,168,56,${intensity})` : FAINT,
                border: m.active ? `1px solid ${GOLD}40` : `1px solid ${GHOST}40`,
                cursor: 'default',
                transition: 'background 0.3s ease',
              }}
            />
            {!compact && (
              <Mo style={{ fontSize: 7, color: 'var(--ghost)', display: 'block', marginTop: 3, letterSpacing: 0.5 }}>{m.label}</Mo>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function StreakWidget({ navigate, user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.getStreaks().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div
      data-testid="streak-widget"
      onClick={() => navigate && navigate('streaks')}
      style={{ padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`, cursor: navigate ? 'pointer' : 'default' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="str-b">{Ic.flame(16, GOLD)}</span>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: 0.5 }}>
            {data.current_streak} MONTH STREAK
          </Mo>
        </div>
        <Mo style={{ fontSize: 10, color: 'var(--ghost)', letterSpacing: 1 }}>
          BEST: {data.longest_streak}
        </Mo>
      </div>
      <HeatmapGrid heatmap={data.heatmap} compact />
    </div>
  );
}

export function StreakScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStreaks = useCallback(() => {
    setLoading(true);
    api.getStreaks().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStreaks(); }, [fetchStreaks]);

  if (loading || !data) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center' }}>
        <Mo style={{ color: 'var(--mute)' }}>Loading streak data...</Mo>
      </div>
    );
  }

  return (
    <div data-testid="streak-screen" className="dettio-screen">
      <Horizon intensity={0.7} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>{Ic.flame(40, GOLD)}</div>
          <Dp style={{ fontSize: 48, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2 }}>
            {data.current_streak}
          </Dp>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2 }}>MONTH STREAK</Mo>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[
            { l: 'CURRENT', v: `${data.current_streak}mo`, c: GOLD },
            { l: 'LONGEST', v: `${data.longest_streak}mo`, c: LIGHT },
            { l: 'ACTIVE', v: `${data.total_active_months}mo`, c: GOLD },
            { l: 'TOTAL PAID', v: `$${data.total_paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, c: GOLD },
          ].map(s => (
            <div key={s.l} style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center' }}>
              <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
              <Mo style={{ fontSize: 13, fontWeight: 700, color: s.c }}>{s.v}</Mo>
            </div>
          ))}
        </div>

        <Label>PAYMENT ACTIVITY</Label>
        <div style={{ padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 24 }}>
          <HeatmapGrid heatmap={data.heatmap} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <Mo style={{ fontSize: 7, color: 'var(--ghost)' }}>Less</Mo>
            {[0, 0.2, 0.5, 0.8, 1].map((a, i) => (
              <div key={i} style={{
                width: 10, height: 10, borderRadius: 2,
                background: a === 0 ? FAINT : `rgba(232,168,56,${a})`,
              }} />
            ))}
            <Mo style={{ fontSize: 7, color: 'var(--ghost)' }}>More</Mo>
          </div>
        </div>

        <Label>STREAK MILESTONES</Label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
          {data.milestones.map(m => (
            <div
              key={m.name}
              data-testid={`milestone-${m.name.toLowerCase().replace(/\s/g, '-')}`}
              style={{
                padding: 16, textAlign: 'center',
                background: m.earned ? `${TIER_COLORS[m.tier]}10` : FAINT,
                border: `1px solid ${m.earned ? TIER_COLORS[m.tier] + '40' : GHOST + '40'}`,
                opacity: m.earned ? 1 : 0.5,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                {m.earned ? Ic.trophy(28, TIER_COLORS[m.tier]) : Ic.trophy(28, GHOST)}
              </div>
              <Mo style={{
                fontSize: 9, fontWeight: 700, display: 'block', marginBottom: 4,
                color: m.earned ? TIER_COLORS[m.tier] : GHOST, letterSpacing: 0.5,
              }}>
                {m.name.toUpperCase()}
              </Mo>
              <Bd style={{ fontSize: 10, color: m.earned ? MUTE : GHOST, display: 'block' }}>
                {m.months} months
              </Bd>
              {m.earned && m.earned_at && (
                <Mo style={{ fontSize: 7, color: 'var(--ghost)', display: 'block', marginTop: 4 }}>
                  Earned {m.earned_at}
                </Mo>
              )}
              {!m.earned && (
                <div style={{ marginTop: 6, height: 3, background: 'var(--ghost)' + '30', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${Math.min(100, (data.longest_streak / m.months) * 100)}%`,
                    background: TIER_COLORS[m.tier] + '60',
                  }} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding: 14, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)', letterSpacing: 0.5, lineHeight: 1.6 }}>
            {data.current_streak === 0
              ? "Make a payment this month to start your streak. Consistency is the key to debt freedom."
              : data.current_streak >= 12
                ? `${data.current_streak} months of consistency. You're unstoppable. Keep this legendary streak going.`
                : `${data.current_streak} months strong. ${Math.max(0, 3 - data.current_streak) > 0
                    ? `${3 - data.current_streak} more months to unlock Quarter Crusher!`
                    : data.current_streak < 6
                      ? `${6 - data.current_streak} more months to unlock Half Year Hero!`
                      : `${12 - data.current_streak} more months to unlock Year of Freedom!`}`
            }
          </Mo>
        </div>
      </div>
    </div>
  );
}
